import { Refresh } from '@mui/icons-material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
// @mui material components
import DownloadIcon from '@mui/icons-material/Download';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SettingsIcon from '@mui/icons-material/Settings';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Box, IconButton, Stack, Tooltip, useMediaQuery } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as ControllerBoxIcon } from 'assets/images/icons/controller-box.svg';
import CsvDownloadWizard from 'components/CsvDownloadWizard';
import DialogSelect from 'components/Dialog/DialogSelect.js';
import GenericModal from 'components/GenericModal';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchControllerRangeData, setControllerTimeRangeValue } from 'stores/sensor/controllersSlice';
import { brandColors } from 'types/colors';
import { TimeInMS } from 'types/time';
import { getControllerRangeData } from 'services/dataService';
import { ControllerChartFields } from 'types/ControllerFields';

import ControllerAlertsModal from './ControllerAlertsModal';
import LastUpdateTime from './LastUpdateTime';
import WeeklySchedule from './WeeklySchedule';

const TimeRangeOptions = [
  // { label: "1 Minute", value: TimeInMS.Minute },
  { label: '1 Hour', value: TimeInMS.Hour },
  { label: 'Today', value: TimeInMS.Day },
  { label: '1 Week', value: TimeInMS.Week },
  { label: '1 Month', value: TimeInMS.Month },
  { label: 'date to date', value: 'date to date' },
];

const topBarTooltipProps = {
  slotProps: {
    popper: {
      sx: {
        zIndex: 40003,
      },
    },
  },
};

const INACTIVE_BACKGROUND_COLOR = '#5e5e5e';
const INACTIVE_THRESHOLD_SECONDS = 900;

const controllerCsvColumnLabels = Object.fromEntries(
  Object.entries(ControllerChartFields).map(([column, field]) => [column, field.display_value]),
);

// Builds the CSV column list based on what this controller can actually measure.
const getControllerCsvColumns = ({ isRelayController, hasRadar }) => {
  // Relay controllers use channels, regular controllers use the normal on/off fields.
  const hiddenFields = isRelayController
    ? ['on_off', 'fan_speed', 'temp_set_point', 'cool_water_valve']
    : ['channel_1', 'channel_2'];

  // Motion is only useful when this controller has radar data.
  if (!hasRadar) {
    hiddenFields.push('motion_counter');
  }

  return Object.entries(ControllerChartFields)
    .filter(([, field]) => !hiddenFields.includes(field.db_col_name))
    .map(([column]) => column);
};

const topBarButtonSx = {
  width: '4rem',
  minWidth: '4rem',
  height: '2rem',
  p: 0,
  // border: '2px solid #fff',
  borderRadius: '7px',
  boxShadow: 'none',
  color: '#fff',
  transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'scale(1)',
  '&:hover': {
    // color: brandColors.BLUE,
    boxShadow: 'none',
    // backgroundColor: '#fff',
    // scale: '1.1',
    transform: 'scale(1.2)',
  },
};

const topBarIconSx = {
  settings: { scale: '1.8' },
  queryStats: { scale: '2.2' },
  download: { scale: '1.5', ml: 0.5 },
  refresh: { fontSize: '1.5rem' },
};

const TopBar = ({ onOpenCharts }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const controller_id = useParams().id;
  const loading = useSelector(state => state.controllers.loading);
  const rangeObj = useSelector(state => state.controllers.timeRange);
  const selectedGraphs = useSelector(state => state.controllers.selectedGraphs);
  const controllerName = useSelector(state => state.controllers.data.controllerName);
  const building = useSelector(state => state.controllers.data.building);
  const controllerData = useSelector(state => state.controllers.data);
  const lastUpdate = useSelector(state => state.controllers.lastUpdate);
  const devices = useSelector(state => state.sensors.devices);
  const controllerDeviceType = useSelector(state => state.controllers.currentMeasurements?.device_type);
  const currentMeasurements = useSelector(state => state.controllers.currentMeasurements);
  const matchedDevice = Array.isArray(devices) ? devices.find(d => String(d?.id) === String(controller_id)) : null;
  const buildingId = controllerData.building_id || currentMeasurements?.building_id || matchedDevice?.building_id;
  const resolvedDeviceType = controllerDeviceType || matchedDevice?.device_type || '';
  const isControllerType = resolvedDeviceType ? resolvedDeviceType.startsWith('Controller') : true;
  // We detect relay controllers from the model first, and fall back to the device type text.
  const isRelayController =
    ['RELAY_2CH', 'RELAY_2CH_NC', 'RELAY_4CH', 'RELAY_4CH_NC'].includes(currentMeasurements?.controller_model) ||
    String(resolvedDeviceType || '')
      .toLowerCase()
      .includes('relay');
  // These are the only controller columns the CSV wizard is allowed to show and export.
  const availableCsvColumns = useMemo(
    () => getControllerCsvColumns({ isRelayController, hasRadar: controllerData.hasRadar }),
    [controllerData.hasRadar, isRelayController],
  );
  const shouldShowSchedule = 1;
  const isInactive =
    ((lastUpdate !== undefined && lastUpdate > 0) || lastUpdate === 0 || lastUpdate === null) &&
    Math.floor(Date.now() / 1000) - Number(lastUpdate) > INACTIVE_THRESHOLD_SECONDS;
  const topBarBackgroundColor = isInactive ? INACTIVE_BACKGROUND_COLOR : brandColors.BLUE;
  const topBarColorSx = {
    backgroundColor: topBarBackgroundColor,
    '&:hover': {
      backgroundColor: topBarBackgroundColor,
    },
  };
  const controllerStatusButtonSx = [topBarButtonSx, topBarColorSx];

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [openModal, setOpenModal] = useState(false);
  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  const [csvWizardOpen, setCsvWizardOpen] = useState(false);

  const handleBuildingClick = () => {
    if (!buildingId) {
      return;
    }
    navigate(`/manage-buildings/${buildingId}`);
  };

  const renderControllerDisplayName = variant => (
    <MDTypography variant={variant} color="light">
      {building ? (
        <>
          <Box
            component="span"
            onClick={handleBuildingClick}
            sx={{
              cursor: buildingId ? 'pointer' : 'inherit',
              textDecoration: 'none',
            }}>
            {building}
          </Box>
          {' / '}
        </>
      ) : null}
      {controllerName || ''}
    </MDTypography>
  );

  const refreshLastUpdateAndLastUpdateTime = () => {
    updateRangeData();
  };

  const handleDownloadCSV = () => setCsvWizardOpen(true);

  const loadCsvMeasurements = (columns, timeframe, options) =>
    getControllerRangeData(columns, controller_id, timeframe, userTimeZone, options?.includeRawRecords);

  const changeTimeRange = range => {
    // console.log("changeTimeRange triggered");
    dispatch(setControllerTimeRangeValue(range));
  };

  const updateRangeData = async () => {
    dispatch(
      await fetchControllerRangeData({
        measurements: selectedGraphs,
        controller_id: controller_id,
        timeRange: rangeObj.value,
        navigate,
        userTimeZone: userTimeZone,
      }),
    );
  };

  const handleCancel = useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleSuccess = useCallback(() => {
    setOpenModal(false);
  }, []);

  const handleOpenAlerts = useCallback(() => {
    setAlertsModalOpen(true);
  }, []);

  const handleCloseAlerts = useCallback(() => {
    setAlertsModalOpen(false);
  }, []);

  const weeklyScheduleProps = useMemo(
    () => ({
      open: openModal,
      onSuccess: handleSuccess,
      onCancel: handleCancel,
    }),
    [openModal, handleSuccess, handleCancel],
  );

  const weeklyScheduleSize = useMemo(
    () => ({
      width: 'min(600px, 92vw)',
      maxHeight: '80vh',
    }),
    [],
  );

  return (
    <MDBox
      mb={1}
      px={2}
      variant="gradient"
      borderRadius="lg"
      coloredShadow="light"
      id="controllerPageTopBar"
      style={{
        background: topBarBackgroundColor,
        marginTop: isSmallScreen ? '3.5rem' : 0,
      }}>
      {openModal && (
        <GenericModal
          open={openModal}
          onClose={handleCancel}
          title="Controller schedule"
          Component={WeeklySchedule}
          componentProps={weeklyScheduleProps}
          size={weeklyScheduleSize}
          closeOnBackdrop={true}
          closeOnEsc={true}
          titleIcon={<CalendarMonthIcon sx={{ scale: '1.8', marginLeft: '.5rem' }} />}
        />
      )}
      {alertsModalOpen && (
        <ControllerAlertsModal
          open={alertsModalOpen}
          onClose={handleCloseAlerts}
          controllerId={controller_id}
          timeRange={rangeObj.value}
          userTimeZone={userTimeZone}
        />
      )}
      <GenericModal
        open={csvWizardOpen}
        onClose={() => setCsvWizardOpen(false)}
        title="Download charts data"
        Component={CsvDownloadWizard}
        componentProps={{
          deviceId: controller_id,
          deviceName: controllerName,
          // Pass filtered columns so relay-only fields do not appear for regular controllers.
          availableColumns: availableCsvColumns,
          selectedColumns: selectedGraphs,
          columnLabels: controllerCsvColumnLabels,
          currentTimeRange: rangeObj.value,
          fetchMeasurements: loadCsvMeasurements,
          onComplete: () => setCsvWizardOpen(false),
        }}
      />
      {isSmallScreen ? (
        <Stack direction="column" alignItems="stretch" justifyContent="space-between" spacing={1} pt={1}>
          <Stack
            direction="column"
            marginLeft={0}
            spacing={2}
            alignItems="flex-start"
            className="top-bar"
            sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex' }}>
              {renderControllerDisplayName('h5')}
              {isControllerType ? (
                <Box
                  component={ControllerBoxIcon}
                  sx={{
                    ml: '0.35rem',
                    mt: '0.2rem',
                    width: '1.3em',
                    height: '1.3em',
                    color: '#fff',
                    '& path': {
                      fill: 'currentColor',
                    },
                  }}
                />
              ) : (
                <Icon fontSize="large" style={{ color: 'white' }}>
                  {isInactive ? 'sensors_off' : 'sensors'}
                </Icon>
              )}
            </Box>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 40px',
              }}>
              {loading ? <CircularProgress aria-label="Loading controller data" style={{ color: 'white' }} /> : null}
            </Box>
          </Stack>
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              width: '100%',
              justifyContent: 'center',
            }}>
            <Box sx={{ flex: '0 0 auto' }}>
              <LastUpdateTime />
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={0}
            className="top-bar-actions"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, max-content)',
              gridAutoRows: 'max-content',
              columnGap: 1,
              rowGap: 1,
              alignItems: 'center',
              width: {
                xs: '100%',
              },
              justifyContent: 'center',
              justifyItems: 'center',
              overflowX: 'hidden',
              whiteSpace: 'nowrap',
              pb: 0.5,
              '& > :not(style) + :not(style)': {
                marginLeft: 0,
              },
            }}>
            <Tooltip {...topBarTooltipProps} title="Controller alerts" placement="top" arrow>
              <Button
                className="top-bar-btn"
                sx={controllerStatusButtonSx}
                color="success"
                variant="contained"
                onClick={handleOpenAlerts}>
                <NotificationImportantIcon sx={{ scale: '1.8' }} />
              </Button>
            </Tooltip>
            {shouldShowSchedule && (
              <Tooltip {...topBarTooltipProps} title="Controller schedule" placement="top" arrow>
                <Button
                  className="top-bar-btn"
                  sx={controllerStatusButtonSx}
                  color="success"
                  variant="contained"
                  onClick={() => setOpenModal(s => !s)}>
                  <CalendarMonthIcon sx={{ scale: '1.8' }} />
                </Button>
              </Tooltip>
            )}
            <Tooltip {...topBarTooltipProps} title="Edit controller" placement="top" arrow>
              <Button
                className="top-bar-btn"
                sx={controllerStatusButtonSx}
                color="success"
                variant="contained"
                onClick={() => navigate(`/manage-device/controller/${controller_id}`)}>
                <SettingsIcon sx={topBarIconSx.settings} />
              </Button>
            </Tooltip>
            <Tooltip {...topBarTooltipProps} title="See charts" placement="top" arrow>
              <Button
                className="top-bar-btn"
                sx={controllerStatusButtonSx}
                color="success"
                variant="contained"
                onClick={onOpenCharts}>
                <TimelineIcon sx={{ scale: '2.4' }} />
                <OpenInNewIcon sx={{ scale: '1.4', ml: 0.2, mt: 1.5 }} />
              </Button>
            </Tooltip>
            <DialogSelect
              buttonTitle="Time Range"
              rangeObj={rangeObj}
              dialogTitle="Choose Time Range"
              color="white"
              onSelectionChanged={changeTimeRange}
              onSelectionOK={setControllerTimeRangeValue}
              selectLabel="Time Range"
              selectOptions={TimeRangeOptions}
              maxDateRangeDays={31}
              default={TimeRangeOptions[1]}
              onClose={refreshLastUpdateAndLastUpdateTime}></DialogSelect>
            <IconButton sx={{}} color="light" onClick={() => refreshLastUpdateAndLastUpdateTime()}>
              <Refresh sx={topBarIconSx.refresh} />
            </IconButton>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack
            direction="row"
            marginLeft={2}
            spacing={2}
            alignItems="end"
            className="top-bar"
            sx={{ display: 'flex', alignItems: 'center' }}>
            {renderControllerDisplayName('h4')}
            {isControllerType ? (
              <Box
                component={ControllerBoxIcon}
                sx={{
                  ml: '0.35rem',
                  width: '1.5em',
                  height: '1.5em',
                  color: '#fff',
                  '& path': {
                    fill: 'currentColor',
                  },
                }}
              />
            ) : (
              <Icon fontSize="large" style={{ color: 'white' }}>
                {isInactive ? 'sensors_off' : 'sensors'}
              </Icon>
            )}
            <LastUpdateTime />
            <Box
              sx={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 40px',
              }}>
              {loading ? <CircularProgress aria-label="Loading controller data" style={{ color: 'white' }} /> : null}
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={0.4}
            sx={{
              alignItems: 'center',
              width: 'fit-content',
              justifyContent: 'flex-end',
            }}>
            <Tooltip {...topBarTooltipProps} title="Controller alerts" placement="top" arrow>
              <Button
                className="top-bar-btn"
                sx={controllerStatusButtonSx}
                color="success"
                variant="contained"
                onClick={handleOpenAlerts}>
                <NotificationImportantIcon sx={{ scale: '2' }} />
              </Button>
            </Tooltip>
            {shouldShowSchedule && (
              <Tooltip {...topBarTooltipProps} title="Controller schedule" placement="top" arrow>
                <Button
                  className="top-bar-btn"
                  sx={controllerStatusButtonSx}
                  color="success"
                  variant="contained"
                  onClick={() => setOpenModal(s => !s)}>
                  <CalendarMonthIcon sx={{ scale: '1.8' }} />
                </Button>
              </Tooltip>
            )}
            <Tooltip {...topBarTooltipProps} title="Edit controller" placement="top" arrow>
              <Button
                className="top-bar-btn"
                sx={controllerStatusButtonSx}
                color="success"
                variant="contained"
                onClick={() => navigate(`/manage-device/controller/${controller_id}`)}>
                <SettingsIcon sx={topBarIconSx.settings} />
              </Button>
            </Tooltip>
            <Tooltip {...topBarTooltipProps} title="Download charts data as CSV" placement="top" arrow>
              <Button
                className="top-bar-btn"
                sx={controllerStatusButtonSx}
                color="success"
                variant="contained"
                onClick={handleDownloadCSV}>
                <QueryStatsIcon sx={topBarIconSx.queryStats}></QueryStatsIcon>
                <DownloadIcon sx={topBarIconSx.download}></DownloadIcon>
              </Button>
            </Tooltip>
            <DialogSelect
              buttonTitle="Time Range"
              rangeObj={rangeObj}
              dialogTitle="Choose Time Range"
              color="white"
              onSelectionChanged={changeTimeRange}
              onSelectionOK={setControllerTimeRangeValue}
              selectLabel="Time Range"
              selectOptions={TimeRangeOptions}
              maxDateRangeDays={31}
              default={TimeRangeOptions[1]}
              onClose={refreshLastUpdateAndLastUpdateTime}></DialogSelect>
            <IconButton sx={{ minWidth: '30px' }} color="light" onClick={() => refreshLastUpdateAndLastUpdateTime()}>
              <Refresh sx={topBarIconSx.refresh} />
            </IconButton>
          </Stack>
        </Stack>
      )}
    </MDBox>
  );
};

TopBar.propTypes = {
  onOpenCharts: PropTypes.func,
};

export default TopBar;
